import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { UserManager, User, Log } from 'oidc-client';
import * as mojoIdServer from '../modules/mojo-identity-server';
import { BehaviorSubject, Observable, empty, from, combineLatest } from 'rxjs';
import { filter, flatMap, takeWhile, catchError, take, tap } from 'rxjs';
import { UserProfile } from '../models/user-profile';
import { RoleHttpService } from '../../roles/services/role-http.service';
import { CurrentProductService } from '../../product/services/current-product.service';
import { UrlBuilderService } from '../../common/services/url-builder.service';
import { ProductInfo } from '../../product/services/current-product.service';
import { UserSettingsHttpService } from '@feature/user-settings/services/user-settings-http.service';
import { UserService } from '@feature/core/user/user.service';
import { UserSettings } from '@feature/user-settings/models/user-settings';
import { User as NewUser } from '@feature/core/user/user.types';
import { EntityKey } from '@feature/pma-entity/pma-entity-types';


/**
 * Implementation of OidcClient auth. See: https://github.com/IdentityModel/oidc-client-js/wiki
 */
@Injectable({
  providedIn: 'root'
})
export class OidcAuthService implements OnDestroy {
  private manager: UserManager;
  private user: User = null;
  private loggedIn: boolean;
  private isAlive = true;
  private productInfo: ProductInfo;
  private isSigningIn: boolean = false;
  /**
   *  Raised when a user session has been established (or re-established).
   */
  userLoadedEvent: EventEmitter<User> = new EventEmitter<User>();

  private userProfile = new BehaviorSubject<UserProfile>(null);
  private userLoggedIn = new BehaviorSubject(false);

  userIsLoggedIn(): Observable<boolean> {
    return this.userLoggedIn.asObservable();
  }

/**
 * Returns the claims attached to the user, available on the profile property on the User object.
 * */
  getUserProfile(): Observable<UserProfile> {
    return this.userProfile.asObservable();
  }

  getUserProfileImmediate(): UserProfile {
    return this.userProfile.value;
  }

  getProductInfo(): ProductInfo {
    return this.productInfo;
  }

  private setUserProfile(user: User) {
    let profile = null;
    if (user) {
      profile = mojoIdServer.userProfileFromAny(user.profile);
    }
    this.userProfile.next(profile);
  }

  get currentUser(): User {
    return this.user;
  }

  get isLoggedIn(): boolean {
    return this.loggedIn;
  }

  constructor(
    private readonly urlBuilder: UrlBuilderService,
    private readonly productService: CurrentProductService,
    private readonly roleHttpService: RoleHttpService,
    private readonly userSettingsHttpService: UserSettingsHttpService,
    private readonly userService: UserService) {

    // DEVS
    // If the following line is uncommented then oidc client & other logging will be enabled for console
    // Log.logger = console;

    this.manager = new UserManager(mojoIdServer.getClientSettings());

    const getUser = from(this.manager.getUser());

    getUser.pipe(takeWhile(() => this.isAlive))
      .subscribe((user: User) => {
        this.user = user;
      });

    this.manager.events.addUserSignedOut(async () => {
      Log.logger.info('user addUserSignedOut event received');
      if (this.user && this.loggedIn) {
        Log.logger.info('calling signoutRedirect');
        await this.manager.signoutRedirect();
      }
    });

    this.manager.events.addUserLoaded((user) => {
      Log.logger.info('user addUserLoaded event received');
      this.productService
        .getProductPlatform().pipe(take(1))
        .subscribe(productInfo => {
          this.productInfo = productInfo;
          this.userService.retrieveUser(user);          
        });
    });

    this.manager.events.addUserUnloaded(() => {
      Log.logger.info('user addUserUnloaded event received');
      this.loggedIn = false;
      this.userLoggedIn.next(false);
      this.isSigningIn = false;
    });

  }

  /**
   * returns an authorization header from the User object. This is made from the type of token (e.g. Bearer) and the access token itself.
   * */
  getAuthorizationHeaderValue(): string {
    if (!this.user) {
      return null;
    }
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  logout(): void {
    Log.logger.info('explicit client logout');
    this.manager.clearStaleState();
    this.manager.signoutRedirect();
  }

  changePassword(returnUrl: string): void {

    const productInfoQueryString = `productId=${this.productInfo.product}&brandId=${this.productInfo.brand}&platformId=${this.productInfo.platform}`;
    const url = this.urlBuilder.getOidcClientAuthorityUrl(
      `ChangePassword?${productInfoQueryString}&returnUrl=${encodeURIComponent(returnUrl)}`
    );

    window.location.href = url;
}
  
  /**
   * To do the heavy lifting for protocol interaction, we now need startAuthentication and completeAuthentication. These will handle the OpenID Connect authentication requests for us, using the oidc-client signinRedirect and signinRedirectCallback methods which, when called upon, will automatically redirect users to our OpenID Connect provider using requests configured by our UserManagerSettings. An alternative to this would be to use signinPopup and signinPopupCallback which will open a new window for the request instead of a redirection.
   */
  startAuthentication(roundTripState: any): Observable<void> {
    if (this.isSigningIn) {
      Log.logger.warn('startAuthentication ignored , sign in already in process');
      return new Observable<void>(observer => observer.complete());
    }
    Log.logger.info('startAuthentication Initiating sign in process');
    this.isSigningIn = true;
    return this.productService
      .getProductPlatform()
      .pipe(
      flatMap(productInfo => {
        this.productInfo = productInfo;
        return from(this.manager.signinRedirect({
          data: roundTripState,
          acr_values: `productId:${productInfo.product} brandId:${productInfo.brand} platformId:${productInfo.platform}`
        }));
      })
      );
  }

  completeAuthentication(): Observable<boolean> {

    const authComplete = new BehaviorSubject<boolean>(false);
    const signinRedirectCallback = from(this.manager.signinRedirectCallback());
    signinRedirectCallback.pipe(take(1))
      .subscribe(((user: User) => {

        this.user = user;

        if (this.user) {
          const getRoles = from(this.roleHttpService.getRoles());

          combineLatest(getRoles.pipe(catchError(err => {
            console.error("");
            return empty();
          })), this.productService.getProductPlatform()).pipe(take(1))
            .subscribe(([roles, productInfo]) => {
              this.user.profile["roles"] = roles;
              this.setUserProfile(this.user);
              this.loggedIn = true;
              this.userLoadedEvent.emit(this.user);
              this.userLoggedIn.next(true);
              this.isSigningIn = false;
              authComplete.next(true);
              this.productInfo = productInfo;
            });
        }
      }));
    return authComplete;
  }

  

  ngOnDestroy(): void {
    this.isAlive = false;
  }


}
